import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Pencil, Gift, ExternalLink, Eye, EyeOff, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import ZButton from "@/components/ZButton";
import { ZInput, ZTextarea } from "@/components/ZInput";
import Modal from "@/components/Modal";
import ShareCelebrationModal from "@/components/ShareCelebrationModal";
import GiftImportModal from "@/components/GiftImportModal";

const MODE_LABELS = {
  reservation: "Réservation seule",
  contribution: "Contribution monétaire",
  any: "Réservation OU contribution",
};
const STYLE_LABELS = { single: "Prix unique (1 acheteur)", pooled: "Cumulable (objectif)" };

export default function OrgGiftListPage() {
  const { id: eventId } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [defaultCats, setDefaultCats] = useState([]);

  // Item editor
  const [showItemModal, setShowItemModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null); // item being edited
  const [form, setForm] = useState(blankItem());
  const [savingItem, setSavingItem] = useState(false);

  // List meta editor
  const [showListEdit, setShowListEdit] = useState(false);
  const [meta, setMeta] = useState({ title: "", description: "", welcome_message: "", footer_message: "" });
  const [savingMeta, setSavingMeta] = useState(false);
  // Share modal
  const [shareOpen, setShareOpen] = useState(false);

  function blankItem() {
    return {
      category: "Maison",
      name: "",
      description: "",
      image_url: "",
      purchase_url: "",
      price: "",
      mode: "any",
      contrib_style: "pooled",
      quantity: 1,
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const [evR, defR] = await Promise.all([
          api.get(`/organizer/events`),
          api.get(`/gift-lists/categories/defaults`),
        ]);
        const ev = (evR.data || []).find((e) => e.id === eventId);
        setEvent(ev || null);
        setDefaultCats(defR.data.categories || []);
        try {
          const r = await api.get(`/gift-lists/event/${eventId}`);
          setList(r.data);
        } catch (e) {
          if (e?.response?.status !== 404) throw e;
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post(`/gift-lists`, {
        event_id: eventId,
        title: `Liste de cadeaux — ${event?.title || "Notre événement"}`,
        description: "",
      });
      setList(r.data);
      toast.success("Liste créée. Ajoutez vos premiers cadeaux !");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCreating(false);
    }
  };

  const togglePublish = async () => {
    const next = list.status === "published" ? "draft" : "published";
    try {
      const r = await api.put(`/gift-lists/${list.id}`, { status: next });
      setList(r.data);
      toast.success(next === "published" ? "Liste publiée — lien public actif." : "Liste repassée en brouillon.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeList = async () => {
    if (!window.confirm("Supprimer définitivement la liste et tous ses items ?")) return;
    try {
      await api.delete(`/gift-lists/${list.id}`);
      setList(null);
      toast.success("Liste supprimée.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openAdd = (cat) => {
    setEditing(null);
    setForm({ ...blankItem(), category: cat || "Maison" });
    setShowItemModal(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      category: it.category,
      name: it.name,
      description: it.description || "",
      image_url: it.image_url || "",
      purchase_url: it.purchase_url || "",
      price: String(it.price || ""),
      mode: it.mode,
      contrib_style: it.contrib_style,
      quantity: it.quantity || 1,
    });
    setShowItemModal(true);
  };

  const saveItem = async () => {
    if (!form.name.trim()) { toast.error("Nom du cadeau requis."); return; }
    if (form.mode === "contribution" && (!form.price || Number(form.price) <= 0)) {
      toast.error("Indiquez un prix cible > 0 pour une cagnotte (contribution monétaire)."); return;
    }
    setSavingItem(true);
    try {
      const payload = {
        category: form.category,
        name: form.name.trim(),
        description: form.description.trim(),
        image_url: form.image_url.trim() || null,
        purchase_url: form.purchase_url.trim() || null,
        price: Number(form.price || 0),
        mode: form.mode,
        contrib_style: form.contrib_style,
        quantity: Number(form.quantity || 1),
      };
      if (editing) {
        await api.put(`/gift-lists/${list.id}/items/${editing.id}`, payload);
      } else {
        await api.post(`/gift-lists/${list.id}/items`, payload);
      }
      const r = await api.get(`/gift-lists/${list.id}`);
      setList(r.data);
      setShowItemModal(false);
      toast.success(editing ? "Item mis à jour." : "Item ajouté.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingItem(false); }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Supprimer « ${it.name} » ?`)) return;
    try {
      await api.delete(`/gift-lists/${list.id}/items/${it.id}`);
      const r = await api.get(`/gift-lists/${list.id}`);
      setList(r.data);
      toast.success("Item supprimé.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const openListEdit = () => {
    setMeta({
      title: list.title,
      description: list.description || "",
      welcome_message: list.welcome_message || "",
      footer_message: list.footer_message || "",
    });
    setShowListEdit(true);
  };
  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const r = await api.put(`/gift-lists/${list.id}`, meta);
      setList(r.data);
      setShowListEdit(false);
      toast.success("Liste mise à jour.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingMeta(false); }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/liste/${list.slug}`;
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié."); }
    catch { toast.error("Impossible de copier."); }
  };

  const itemsByCategory = useMemo(() => {
    const map = {};
    (list?.items || []).forEach((it) => {
      (map[it.category] = map[it.category] || []).push(it);
    });
    return map;
  }, [list]);

  if (loading) return <div style={{ padding: 24 }}>Chargement…</div>;

  // No list yet — show creation
  if (!list) {
    return (
      <div style={{ padding: "20px 16px 80px", maxWidth: 720, margin: "0 auto" }}>
        <button onClick={() => nav(`/org/event/${eventId}`)} className="z-link" data-testid="back-btn"
          style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
          <ArrowLeft size={16} /> Retour
        </button>
        <div style={{ textAlign: "center", padding: "60px 20px", background: "white", borderRadius: 16, border: "1px solid #E5E7EB" }}>
          <Gift size={48} color="#DA7101" style={{ margin: "0 auto 16px" }} />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#01696F", marginBottom: 8 }}>
            Créer une liste de cadeaux
          </h1>
          <p style={{ color: "#5B6671", fontSize: 14, marginBottom: 22, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
            Proposez à vos invités une liste de cadeaux personnalisée — réservation, contribution monétaire ou les deux. Chaque cadeau peut être à prix unique ou cumulable.
          </p>
          <ZButton onClick={create} loading={creating} data-testid="create-list-btn">
            Créer la liste pour « {event?.title || "cet événement"} »
          </ZButton>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 16px 80px", maxWidth: 880, margin: "0 auto" }}>
      <button onClick={() => nav(`/org/event/${eventId}`)} className="z-link" data-testid="back-btn"
        style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <ArrowLeft size={16} /> Retour à l&apos;événement
      </button>

      {/* Header */}
      <div style={{ background: "white", padding: 20, borderRadius: 16, border: "1px solid #E5E7EB", marginBottom: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#FCE2C3", color: "#DA7101", padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, marginBottom: 8 }}>
              <Gift size={12} /> LISTE DE CADEAUX
            </div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#01696F", margin: 0 }} data-testid="list-title">{list.title}</h1>
            {list.description && <p style={{ color: "#5B6671", fontSize: 13, margin: "6px 0 0" }}>{list.description}</p>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={openListEdit} data-testid="edit-list-btn" className="z-btn z-btn-ghost" style={{ fontSize: 13 }}>
              <Pencil size={14} /> Modifier
            </button>
            <button onClick={togglePublish} data-testid="toggle-publish-btn" className="z-btn z-btn-outline" style={{ fontSize: 13 }}>
              {list.status === "published" ? <><EyeOff size={14} /> Dépublier</> : <><Eye size={14} /> Publier</>}
            </button>
            <button onClick={removeList} data-testid="delete-list-btn" className="z-btn z-btn-ghost" style={{ fontSize: 13, color: "#B91C1C" }}>
              <Trash2 size={14} /> Supprimer
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
          <span style={{
            padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700,
            background: list.status === "published" ? "#D6EBEC" : "#F3F4F6",
            color: list.status === "published" ? "#01696F" : "#5B6671",
          }} data-testid="list-status">
            {list.status === "published" ? "Publiée" : list.status === "closed" ? "Fermée" : "Brouillon"}
          </span>
          {list.status === "published" && (
            <>
              <Link to={`/liste/${list.slug}`} target="_blank" rel="noopener" className="z-link" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid="open-public-link">
                <ExternalLink size={14} /> Voir la page publique
              </Link>
              <button onClick={copyPublicLink} className="z-link" data-testid="copy-public-link" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, cursor: "pointer" }}>
                <Share2 size={14} /> Copier le lien
              </button>
              <button onClick={() => setShareOpen(true)} className="z-btn z-btn-primary" style={{ fontSize: 13, padding: "6px 14px" }} data-testid="org-share-list-btn">
                <Share2 size={14} /> Partager sur les réseaux
              </button>
            </>
          )}
        </div>
      </div>

      {/* Items grouped by category */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <ZButton variant="outline" size="sm" onClick={() => setShowImport(true)} data-testid="org-import-gifts-btn">
          <Plus size={15} /> Importer des cadeaux (CSV / Excel / Word / PDF)
        </ZButton>
      </div>
      {list.categories.map((cat) => {
        const items = itemsByCategory[cat] || [];
        if (items.length === 0) {
          return (
            <CategoryHeader key={cat} title={cat} count={0} onAdd={() => openAdd(cat)} dataTestId={`cat-${cat}`}>
              <div style={{ padding: "12px 14px", color: "#9CA3AF", fontSize: 13, fontStyle: "italic" }}>
                Aucun cadeau dans cette catégorie. <button onClick={() => openAdd(cat)} className="z-link" data-testid={`add-item-empty-${cat}`} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#DA7101", fontWeight: 600 }}>Ajouter le premier</button>
              </div>
            </CategoryHeader>
          );
        }
        return (
          <CategoryHeader key={cat} title={cat} count={items.length} onAdd={() => openAdd(cat)} dataTestId={`cat-${cat}`}>
            {items.map((it) => (
              <ItemRow key={it.id} item={it} onEdit={() => openEdit(it)} onDelete={() => deleteItem(it)} />
            ))}
          </CategoryHeader>
        );
      })}

      {/* Floating add */}
      <button onClick={() => openAdd("Maison")} data-testid="floating-add-btn"
        className="z-btn z-btn-primary"
        style={{ position: "fixed", bottom: 90, right: 22, borderRadius: 999, padding: "14px 20px", boxShadow: "0 6px 18px rgba(218,113,1,0.4)" }}>
        <Plus size={18} /> Ajouter un cadeau
      </button>

      {/* Item modal */}
      <Modal open={showItemModal} onClose={() => setShowItemModal(false)}
        title={editing ? "Modifier le cadeau" : "Ajouter un cadeau"}>
        <div style={{ display: "grid", gap: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#5B6671" }}>Catégorie
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              data-testid="item-category"
              style={{ width: "100%", marginTop: 4, padding: 10, border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}>
              {defaultCats.map((c) => <option key={c} value={c}>{c}</option>)}
              <option value="Autre">Autre (personnalisé)</option>
            </select>
          </label>
          {form.category === "Autre" && (
            <ZInput label="Nom de la catégorie personnalisée"
              value={form.customCategory || ""}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Ex: Décoration salon" data-testid="item-custom-category" />
          )}
          <ZInput label="Nom du cadeau *" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex: Service de vaisselle complet" data-testid="item-name" />
          <ZTextarea label="Description" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2} placeholder="Pour des repas conviviaux…" data-testid="item-description" />
          <ZInput label="URL de l'image (optionnel)" value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="https://…" data-testid="item-image" />
          <ZInput label="Lien d'achat externe (optionnel)" value={form.purchase_url}
            onChange={(e) => setForm({ ...form, purchase_url: e.target.value })}
            placeholder="https://amazon.fr/…" data-testid="item-purchase-url" />
          <label style={{ fontSize: 12, fontWeight: 600, color: "#5B6671" }}>Mode
            <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}
              data-testid="item-mode"
              style={{ width: "100%", marginTop: 4, padding: 10, border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}>
              {Object.entries(MODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          {form.mode !== "reservation" && (
            <>
              <ZInput label={form.mode === "contribution" ? "Prix cible (EUR) *" : "Prix cible (EUR) — optionnel"} value={form.price} type="number"
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="300" data-testid="item-price" />
              <label style={{ fontSize: 12, fontWeight: 600, color: "#5B6671" }}>Style de contribution
                <select value={form.contrib_style} onChange={(e) => setForm({ ...form, contrib_style: e.target.value })}
                  data-testid="item-contrib-style"
                  style={{ width: "100%", marginTop: 4, padding: 10, border: "1px solid #D1D5DB", borderRadius: 8, fontSize: 14 }}>
                  {Object.entries(STYLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
            </>
          )}
          {form.mode !== "contribution" && (
            <ZInput label="Quantité disponible" value={form.quantity} type="number"
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              placeholder="1" data-testid="item-quantity" />
          )}
          <ZButton onClick={saveItem} loading={savingItem} block data-testid="item-save-btn">
            {editing ? "Enregistrer" : "Ajouter à la liste"}
          </ZButton>
        </div>
      </Modal>

      {/* List meta modal */}
      <Modal open={showListEdit} onClose={() => setShowListEdit(false)} title="Modifier la liste">
        <div style={{ display: "grid", gap: 12 }}>
          <ZInput label="Titre" value={meta.title}
            onChange={(e) => setMeta({ ...meta, title: e.target.value })} data-testid="meta-title" />
          <ZTextarea label="Description" value={meta.description} rows={2}
            onChange={(e) => setMeta({ ...meta, description: e.target.value })} data-testid="meta-description" />
          <ZTextarea label="Message d'accueil" value={meta.welcome_message} rows={3}
            placeholder="Bienvenue sur notre liste de cadeaux…"
            onChange={(e) => setMeta({ ...meta, welcome_message: e.target.value })} data-testid="meta-welcome" />
          <ZTextarea label="Message de fin (P.S.)" value={meta.footer_message} rows={3}
            onChange={(e) => setMeta({ ...meta, footer_message: e.target.value })} data-testid="meta-footer" />
          <ZButton onClick={saveMeta} loading={savingMeta} block data-testid="meta-save-btn">
            Enregistrer
          </ZButton>
        </div>
      </Modal>

      <ShareCelebrationModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        kind="gift"
        entityId={list.slug}
        title={list.title}
        subtitle={event?.title || `Liste de ${list.organizer_name || ""}`}
        imageUrl={list.cover_image_url || event?.image_url || null}
      />

      <GiftImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        listId={list.id}
        categories={list.categories || []}
        onImported={() => { api.get(`/gift-lists/${list.id}`).then((r) => setList(r.data)); }}
      />
    </div>
  );
}

function CategoryHeader({ title, count, onAdd, children, dataTestId }) {
  return (
    <div style={{ background: "white", borderRadius: 14, border: "1px solid #E5E7EB", marginBottom: 14, overflow: "hidden" }} data-testid={dataTestId}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: "1px solid #F3F4F6", background: "#FAFAF7" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <strong style={{ color: "#01696F", fontSize: 14 }}>{title}</strong>
          <span style={{ fontSize: 11, color: "#5B6671", background: "#F3F4F6", padding: "2px 8px", borderRadius: 999 }}>{count}</span>
        </div>
        <button onClick={onAdd} className="z-btn z-btn-ghost" style={{ fontSize: 12, padding: "4px 10px" }} data-testid={`add-item-${title}`}>
          <Plus size={13} /> Ajouter
        </button>
      </div>
      <div>{children}</div>
    </div>
  );
}

function ItemRow({ item, onEdit, onDelete }) {
  const statusColors = {
    available: { bg: "#D6EBEC", color: "#01696F", label: "Disponible" },
    reserved: { bg: "#FEF3C7", color: "#92400E", label: "Réservé" },
    funded: { bg: "#DCFCE7", color: "#166534", label: "Financé" },
  };
  const s = statusColors[item.status] || statusColors.available;

  return (
    <div style={{ display: "flex", gap: 12, padding: "12px 16px", borderTop: "1px solid #F3F4F6", alignItems: "center" }}
         data-testid={`item-row-${item.id}`}>
      {item.image_url ? (
        <img src={item.image_url} alt={item.name} style={{ width: 56, height: 56, borderRadius: 8, objectFit: "cover" }} />
      ) : (
        <div style={{ width: 56, height: 56, borderRadius: 8, background: "#F3F4F6", display: "grid", placeItems: "center" }}>
          <Gift size={20} color="#9CA3AF" />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#0F1418", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginTop: 4 }}>
          {item.price > 0 && (
            <span style={{ fontSize: 13, fontWeight: 700, color: "#DA7101" }}>{item.price.toFixed(0)} €</span>
          )}
          <span style={{ fontSize: 11, color: s.color, background: s.bg, padding: "2px 8px", borderRadius: 999, fontWeight: 600 }}>
            {s.label}
          </span>
          {item.mode !== "reservation" && item.contrib_style === "pooled" && item.price > 0 && (
            <span style={{ fontSize: 11, color: "#5B6671" }}>{item.raised.toFixed(0)}€ / {item.price.toFixed(0)}€ ({item.progress_pct}%)</span>
          )}
        </div>
      </div>
      <div style={{ display: "flex", gap: 4 }}>
        <button onClick={onEdit} className="z-btn z-btn-ghost" style={{ padding: 6 }} aria-label="Modifier" data-testid={`item-edit-${item.id}`}>
          <Pencil size={14} />
        </button>
        <button onClick={onDelete} className="z-btn z-btn-ghost" style={{ padding: 6, color: "#B91C1C" }} aria-label="Supprimer" data-testid={`item-delete-${item.id}`}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
